import React, {Component} from 'react'
import {CircularProgress, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, ToggleButton, ToggleButtonGroup} from '@mui/material'
import {connect} from 'react-redux'
import {ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip} from 'recharts'

import {getBalanceUsageAll} from '../../actions/balanceUsage'
import Table from '../../components/Table'
import {convertToLocale} from '../../lib/utils'

const TYPE_COLOR = {transactional: '#2196f3', otp: '#4caf50', promotional: '#ff9800'}

// Balances are WHOLE CREDITS, not cents — creditsBalance came back as
// "85696.00000" against a separate balanceCents field, and the admin panel
// shows the same figure unscaled. No divide-by-100 here.
// Field names are protojson's lowerCamelCase with NO underscores — credits30d,
// avgDaily7d — not the Go field spellings (Credits_30D). Getting this wrong
// reads undefined and renders 0, which looks like "no consumption" rather than
// an error. int64 also arrives as a STRING, hence Number() everywhere.
const n = (v) => Number(v || 0)

// Runway from a balance and a daily rate. Returns null when nothing is being
// consumed — an idle wallet has no meaningful depletion date and must not be
// rendered as "0 days".
const runway = (balance, rate) => (rate > 0 ? balance / rate : null)

const runwayColor = (d) => (d === null ? 'default' : d <= 7 ? 'error' : d <= 30 ? 'warning' : 'success')

const addDays = (iso, i) => {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + i)
  return d.toISOString().slice(0, 10)
}

class BalanceNotifications extends Component {
  constructor(props) {
    super(props)
    this.state = {rows: [], windowStart: '', loading: true, withBalanceOnly: false, open: false, selected: null, notice: ''}
    this.seen = false
  }

  componentDidMount() { this.props.getBalanceUsageAll(30, this.state.withBalanceOnly) }

  componentWillReceiveProps(next) {
    const r = next.usageAllResponse
    if (!this.seen && r && Object.keys(r).length) {
      this.seen = true
      const d = r.data || r
      if (r.success !== false) {
        // usageAvailable false means the consumption query FAILED. That looks
        // identical to a quiet fleet (both give an empty list), so say which.
        const notice = d.usageAvailable === false
          ? 'Consumption data is unavailable (the usage query failed).'
          : d.balancesAvailable === false
            ? 'Balances are unavailable right now — consumption figures below are still accurate.'
            : ''
        this.setState({rows: d.orgs || [], windowStart: d.windowStart || '', loading: false, notice})
      } else {
        this.setState({loading: false, notice: r.err?.msg || 'Could not load.'})
      }
    }
  }

  toggle = (e, val) => {
    if (val === null) return
    this.seen = false
    this.setState({withBalanceOnly: val, loading: true}, () => this.props.getBalanceUsageAll(30, val))
  }

  // The daily array ships with every row, so opening the chart costs no request.
  chartData = () => {
    const row = this.state.selected
    if (!row) return []
    return (row.dailyCredits || []).map((c, i) => ({date: addDays(this.state.windowStart, i), credits: n(c)}))
  }

  render() {
    const {rows, loading, withBalanceOnly, open, selected, notice} = this.state
    const isMobile = window.isMobile && window.isMobile()

    return (
      <div className="sms-parent-container" style={isMobile ? {padding: 8} : {}}>
        <div className="sms-small-container">
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 8}}>
            <h2 style={{margin: 0}}>Balance Notifications</h2>
            <ToggleButtonGroup size="small" exclusive value={withBalanceOnly} onChange={this.toggle}>
              <ToggleButton value={false}>All</ToggleButton>
              <ToggleButton value={true}>With balance</ToggleButton>
            </ToggleButtonGroup>
          </div>
          <p style={{marginTop: 0, color: '#666', fontSize: 13}}>
            One row per org and wallet, soonest to run out first. Click any column header to re-sort, or a row for its daily chart.
          </p>
          {notice && <p style={{color: '#c62828', fontSize: 13}}>{notice}</p>}

          {loading ? (
            <div style={{display: 'flex', justifyContent: 'center', padding: 50}}><CircularProgress size={80} thickness={5} /></div>
          ) : rows.length > 0 ? (
            <Table
              onRowClick={(row) => this.setState({open: true, selected: row})}
              // Soonest to run out first — 0 d, then 1 d, then 3 d. The server
              // sends heaviest-consumption-first, but a big spender with months
              // of balance is not who needs a call today. idle wallets have no
              // runway and sort to the bottom.
              defaultSort={{key: 'Runway (7d rate)', dir: 'asc'}}
              column={[
                {
                  name: 'Organisation',
                  value: 'orgName',
                  // Name first, id kept underneath: the id is what you paste
                  // into a query or a support ticket, so it must stay visible.
                  cell: (v, row) => (
                    <div>
                      <div style={{fontWeight: 500}}>{v || '(unnamed)'}</div>
                      <div style={{fontSize: 11, color: '#777'}}>{row?.orgId}</div>
                    </div>
                  ),
                  sortable: true,
                  // Sort by what is on screen, so an unnamed org sorts with the
                  // other blanks rather than by an id nobody is reading.
                  sortValue: (row) => row?.orgName || ''
                },
                {name: 'Wallet', value: 'messageType', cell: (v) => <Chip size="small" label={v} sx={{bgcolor: TYPE_COLOR[v] || '#9e9e9e', color: '#fff'}} />, sortable: true},
                // Every figure below is an int64 that arrives as a STRING —
                // n() before comparing, or 9 sorts above 85696.
                {name: 'Balance', value: 'creditsBalance', cell: (v) => convertToLocale(n(v)), sortable: true, sortDefault: 'desc', sortValue: (row) => n(row?.creditsBalance)},
                {name: 'Avg Daily (7d)', value: 'avgDaily7d', cell: (v) => convertToLocale(Math.round(n(v))), sortable: true, sortDefault: 'desc', sortValue: (row) => n(row?.avgDaily7d)},
                {name: 'Avg Daily (30d)', value: 'avgDaily30d', cell: (v) => convertToLocale(Math.round(n(v))), sortable: true, sortDefault: 'desc', sortValue: (row) => n(row?.avgDaily30d)},
                {name: 'Used (30d)', value: 'credits30d', cell: (v) => convertToLocale(n(v)), sortable: true, sortDefault: 'desc', sortValue: (row) => n(row?.credits30d)},
                {name: 'Active Days', value: 'activeDays30d', cell: (v, row) => `${n(v)} / ${(row?.dailyCredits || []).length}`, sortable: true, sortDefault: 'desc', sortValue: (row) => n(row?.activeDays30d)},
                {
                  name: 'Runway (7d rate)',
                  value: 'creditsBalance',
                  cell: (v, row) => {
                    const d = runway(n(v), n(row?.avgDaily7d))
                    return d === null
                      ? <Chip size="small" label="idle" />
                      : <Chip size="small" color={runwayColor(d)} label={`${Math.floor(d)} d`} />
                  },
                  sortable: true,
                  // The runway is derived from two fields, so it must sort on
                  // the derived number, not on creditsBalance. idle returns
                  // null and the table parks blanks at the bottom either way —
                  // ascending therefore opens on who runs out first, which is
                  // the reason to sort this column at all.
                  sortValue: (row) => runway(n(row?.creditsBalance), n(row?.avgDaily7d))
                },
                // ISO dates (YYYY-MM-DD) sort correctly as text.
                {name: 'Last Used', value: 'lastUsageDate', cell: (v) => v || '-', sortable: true, sortDefault: 'desc'}
              ]}
              data={rows}
              showPagination
              serialNumber
              defaultPageSize={100}
              pageOptions={[50, 100, 200]}
            />
          ) : (
            <p style={{padding: 50, fontSize: 16, color: '#333'}}>No consumption in this window.</p>
          )}
        </div>

        <Dialog open={open} onClose={() => this.setState({open: false})} maxWidth="md" fullWidth>
          <DialogTitle>
            {selected?.orgName || selected?.orgId} · {selected?.messageType}
            <div style={{fontSize: 13, color: '#666', fontWeight: 400}}>{selected?.orgId} · daily credit consumption</div>
          </DialogTitle>
          <DialogContent dividers>
            {selected && (() => {
              const a7 = n(selected.avgDaily7d), a30 = n(selected.avgDaily30d)
              const bursty = a7 > 0 && a30 > 0 && Math.max(a7, a30) / Math.min(a7, a30) >= 2
              return (
                <>
                  <div style={{display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 12}}>
                    <div><div style={{fontSize: 12, color: '#666'}}>Balance</div><div style={{fontSize: 20}}>{convertToLocale(n(selected.creditsBalance))}</div></div>
                    <div><div style={{fontSize: 12, color: '#666'}}>Avg daily (7d)</div><div style={{fontSize: 20}}>{convertToLocale(Math.round(a7))}</div></div>
                    <div><div style={{fontSize: 12, color: '#666'}}>Avg daily (30d)</div><div style={{fontSize: 20}}>{convertToLocale(Math.round(a30))}</div></div>
                    <div><div style={{fontSize: 12, color: '#666'}}>Used (30d)</div><div style={{fontSize: 20}}>{convertToLocale(n(selected.credits30d))}</div></div>
                  </div>
                  {/* A wide gap between the two averages means the sender is
                      bursty, so a single runway number is misleading. Say so
                      rather than implying precision the data cannot support. */}
                  {bursty && (
                    <p style={{fontSize: 12, color: '#ef6c00', marginTop: 0}}>
                      Bursty sender — the 7d and 30d averages differ by more than 2x. Treat the runway as a range, not a date.
                    </p>
                  )}
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={this.chartData()} margin={{top: 8, right: 8, left: 0, bottom: 8}}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{fontSize: 11}} interval="preserveStartEnd" />
                      <YAxis tick={{fontSize: 11}} />
                      <Tooltip formatter={(v) => convertToLocale(v)} />
                      <Bar dataKey="credits" name="credits" fill={TYPE_COLOR[selected.messageType] || '#2196f3'} />
                    </BarChart>
                  </ResponsiveContainer>
                </>
              )
            })()}
          </DialogContent>
          <DialogActions><Button onClick={() => this.setState({open: false})}>Close</Button></DialogActions>
        </Dialog>
      </div>
    )
  }
}

function mapStateToProps(state) {
  return {usageAllResponse: state.balanceUsage.usageAllResponse}
}

export default connect(mapStateToProps, {getBalanceUsageAll})(BalanceNotifications)
